/** One conversation: persisted messages plus the turn that is streaming right now. */
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantLink, ProposalCard, UiAction } from '@/lib/api/types';
import { t } from '@/lib/i18n';
import { isSafeAppPath } from '@/lib/search';
import { SHOW_ON_MAP_EVENT } from '@/ui';
import { aiKeys, createThread, streamMessage, useThread } from './api';
import { getAssistantContext, toPagePayload } from './context';
import type { AssistantEvent } from './sse';
import { setThread } from './store';

export interface ToolChip {
  id: string;
  name: string;
  status: string;
  summary?: string | null;
}

export interface LiveTurn {
  userText: string;
  messageId?: string;
  text: string;
  tools: ToolChip[];
  links: AssistantLink[];
  proposals: ProposalCard[];
  notes: string[];
  status: 'streaming' | 'done' | 'error';
  error?: string;
}

export function useConversation(threadId: string | null, projectId: string | undefined) {
  const qc = useQueryClient();
  const router = useRouter();
  const [live, setLive] = useState<LiveTurn | null>(null);
  // This tab's own turn arrives over its stream; a reply written elsewhere is polled for.
  const thread = useThread(threadId, { poll: live?.status !== 'streaming' });
  const abort = useRef<AbortController | null>(null);

  // Drop the live turn once the persisted thread contains it (no flicker).
  const persistedIds = thread.data?.messages.map((m) => m.id) ?? [];
  const liveId = live?.messageId;
  const livePersisted = live?.status === 'done' && !!liveId && persistedIds.includes(liveId);
  useEffect(() => {
    if (livePersisted) setLive(null);
  }, [livePersisted]);

  const onUi = useCallback(
    (action: UiAction) => {
      const path = action.path;
      if (action.action === 'navigate' && path && isSafeAppPath(path)) {
        void router.navigate({ to: path as '/' });
        return t('Opened {page}', { page: action.label || path });
      }
      if (action.action === 'show_on_map') {
        window.dispatchEvent(new CustomEvent(SHOW_ON_MAP_EVENT, { detail: { vehicle_ids: action.vehicle_ids ?? [], place_ids: action.place_ids ?? [] } }));
        return t('Shown on the map');
      }
      return null;
    },
    [router],
  );

  const apply = useCallback(
    (ev: AssistantEvent) => {
      setLive((cur) => {
        if (!cur) return cur;
        switch (ev.type) {
          case 'start':
            return { ...cur, messageId: ev.message_id };
          case 'text':
            return { ...cur, text: cur.text + ev.delta };
          case 'tool': {
            const rest = cur.tools.filter((x) => x.id !== ev.id);
            return { ...cur, tools: [...rest, { id: ev.id, name: ev.name, status: ev.status, summary: ev.summary }] };
          }
          case 'links':
            return { ...cur, links: [...cur.links, ...ev.links] };
          case 'proposal':
            return { ...cur, proposals: [...cur.proposals, ev.proposal] };
          case 'done':
            return { ...cur, status: 'done', messageId: ev.message_id };
          case 'error':
            return { ...cur, status: 'error', error: ev.message };
          default:
            return cur;
        }
      });
      if (ev.type === 'ui') {
        const note = onUi(ev.action);
        if (note) setLive((cur) => (cur ? { ...cur, notes: [...cur.notes, note] } : cur));
      }
    },
    [onUi],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      setLive({ userText: trimmed, text: '', tools: [], links: [], proposals: [], notes: [], status: 'streaming' });
      try {
        const ctx = getAssistantContext();
        let id = threadId;
        if (!id) {
          const first = ctx?.entities?.[0];
          id = await createThread(first ? { type: first.type, id: first.id } : undefined);
          setThread(id);
        }
        const route = window.location.pathname + window.location.search;
        await streamMessage(id, trimmed, toPagePayload(ctx, route, projectId), apply, controller.signal);
        setLive((cur) => (cur && cur.status === 'streaming' ? { ...cur, status: 'done' } : cur));
        await qc.invalidateQueries({ queryKey: aiKeys.thread(id) });
        void qc.invalidateQueries({ queryKey: aiKeys.threads });
        void qc.invalidateQueries({ queryKey: aiKeys.proposals });
      } catch (e) {
        if (controller.signal.aborted) {
          setLive((cur) => (cur ? { ...cur, status: 'done' } : cur));
          return;
        }
        setLive((cur) => (cur ? { ...cur, status: 'error', error: e instanceof Error ? e.message : String(e) } : cur));
      }
    },
    [threadId, projectId, apply, qc],
  );

  const stop = useCallback(() => abort.current?.abort(), []);

  useEffect(() => () => abort.current?.abort(), []);

  return { thread, live, send, stop, streaming: live?.status === 'streaming' };
}
